import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, MessageSquarePlus, AlertTriangle, Lightbulb, Filter } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Button, Spinner, EmptyState } from "@/components/ui";
import { fmtDate } from "@/lib/dates";

// =============================================================================
// FeedbackAdminScreen — /admin/feedback  (T-468)
// Triage what people have reported: broken things and ideas, filed from the
// screen they were standing on. Manager+.
// =============================================================================

const STATUSES = ["new", "triaged", "in_progress", "resolved", "wont_do"] as const;
const STATUS_LABEL: Record<string, string> = {
  new: "New", triaged: "Triaged", in_progress: "In progress", resolved: "Resolved", wont_do: "Won't do",
};

export default function FeedbackAdminScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [kind, setKind]     = useState<"all" | "issue" | "idea">("all");
  const [status, setStatus] = useState<"open" | "all" | string>("open");
  const [openId, setOpenId] = useState<string | null>(null);
  const [note, setNote]     = useState("");

  const canSee = hasRole(user, "manager");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["feedback_admin"],
    enabled: !!canSee,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("feedback")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(400);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  // Who filed it. Separate query so a permission quirk on users can't break the page.
  const { data: userMap = {} } = useQuery({
    queryKey: ["feedback_users"],
    enabled: !!canSee,
    queryFn: async () => {
      const { data } = await supabase.from("users").select("user_id, login_email").limit(200);
      const m: Record<string, string> = {};
      for (const u of (data ?? []) as any[]) m[u.user_id] = u.login_email;
      return m;
    },
  });

  const save = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: any }) => {
      const { error } = await supabase.from("feedback").update(patch).eq("feedback_id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["feedback_admin"] }),
  });

  const filtered = useMemo(() => rows.filter(r => {
    if (kind !== "all" && r.kind !== kind) return false;
    if (status === "open") return r.status !== "resolved" && r.status !== "wont_do";
    if (status !== "all") return r.status === status;
    return true;
  }), [rows, kind, status]);

  const kpi = useMemo(() => ({
    openIssues: rows.filter(r => r.kind === "issue" && r.status !== "resolved" && r.status !== "wont_do").length,
    ideas:      rows.filter(r => r.kind === "idea"  && r.status !== "resolved" && r.status !== "wont_do").length,
    blocking:   rows.filter(r => r.kind === "issue" && r.severity === "high" && r.status !== "resolved" && r.status !== "wont_do").length,
    resolved:   rows.filter(r => r.status === "resolved").length,
  }), [rows]);

  if (!user) return null;
  if (!canSee) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card><EmptyState icon={AlertTriangle} title="Managers and directors only"
          description="Feedback triage is restricted. You can still report problems and ideas from the menu." /></Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto pb-12">
      <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3" type="button">
        <ArrowLeft size={14} /> Home
      </button>

      <h1 className="text-[22px] font-semibold text-text flex items-center gap-2">
        <MessageSquarePlus size={20} className="text-accent" /> Feedback &amp; ideas
      </h1>
      <p className="text-[13px] text-text-muted mt-1 mb-5">
        What people have hit and what they've asked for. Every item carries the screen they were on.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Kpi label="Open issues"    value={kpi.openIssues} tone={kpi.openIssues ? "warn" : undefined} />
        <Kpi label="Stopping work"  value={kpi.blocking}   tone={kpi.blocking ? "danger" : undefined} />
        <Kpi label="Open ideas"     value={kpi.ideas} />
        <Kpi label="Resolved"       value={kpi.resolved} />
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Filter size={13} className="text-text-muted" />
        {(["all", "issue", "idea"] as const).map(k => (
          <Chip key={k} on={kind === k} onClick={() => setKind(k)}>{k === "all" ? "Everything" : k === "issue" ? "Issues" : "Ideas"}</Chip>
        ))}
        <span className="mx-1 text-line">|</span>
        <Chip on={status === "open"} onClick={() => setStatus("open")}>Open</Chip>
        {STATUSES.map(s => <Chip key={s} on={status === s} onClick={() => setStatus(s)}>{STATUS_LABEL[s]}</Chip>)}
        <Chip on={status === "all"} onClick={() => setStatus("all")}>All</Chip>
      </div>

      <Card>
        <div className="p-5">
          {isLoading ? (
            <div className="py-10 flex justify-center"><Spinner /></div>
          ) : filtered.length === 0 ? (
            <EmptyState icon={MessageSquarePlus} title="Nothing here yet"
              description="When someone reports a problem or suggests an idea, it lands here with the screen they were on." />
          ) : (
            <div className="divide-y divide-line/50">
              {filtered.map(r => {
                const isOpen = openId === r.feedback_id;
                return (
                  <div key={r.feedback_id} className="py-3">
                    <button type="button" className="w-full text-left"
                      onClick={() => { setOpenId(isOpen ? null : r.feedback_id); setNote(r.resolution ?? ""); }}>
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5">
                          {r.kind === "idea"
                            ? <Lightbulb size={15} className="text-info" />
                            : <AlertTriangle size={15} className={r.severity === "high" ? "text-danger" : "text-warn"} />}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] text-text">{r.title}</div>
                          <div className="text-[11px] text-text-dim mt-0.5">
                            {STATUS_LABEL[r.status] ?? r.status}
                            {r.context_route ? <> · <span className="font-mono">{r.context_route}</span></> : null}
                            {r.severity ? <> · {r.severity}</> : null}
                            {" · "}{userMap[r.created_by] ?? "unknown"}
                            {" · "}{fmtDate(r.created_at)}
                          </div>
                        </div>
                      </div>
                    </button>

                    {isOpen && (
                      <div className="mt-3 ml-8 space-y-3">
                        {r.body && <p className="text-[12px] text-text-muted whitespace-pre-wrap">{r.body}</p>}
                        {r.user_agent && <p className="text-[10px] text-text-dim break-all">{r.user_agent}</p>}
                        <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                          placeholder="Notes / what was done about it"
                          className="w-full px-3 py-2 rounded border border-line bg-ink text-text text-[12px] resize-none" />
                        <div className="flex items-center gap-2 flex-wrap">
                          {STATUSES.map(s => (
                            <Button key={s} size="sm" variant={r.status === s ? undefined : "secondary"}
                              disabled={save.isPending}
                              onClick={() => save.mutate({
                                id: r.feedback_id,
                                patch: {
                                  status: s,
                                  resolution: note || null,
                                  resolved_at: (s === "resolved" || s === "wont_do") ? new Date().toISOString() : null,
                                },
                              })}>
                              {STATUS_LABEL[s]}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-[11px] border ${on ? "border-accent bg-accent/10 text-text" : "border-line text-text-muted hover:bg-ink-hover"}`}>
      {children}
    </button>
  );
}

function Kpi({ label, value, tone }: { label: string; value: number | string; tone?: "warn" | "danger" }) {
  const c = tone === "danger" ? "text-danger" : tone === "warn" ? "text-warn" : "text-text";
  return (
    <Card><div className="p-4">
      <div className="text-[10px] uppercase tracking-wider text-text-muted font-mono">{label}</div>
      <div className={`mt-1 text-[18px] font-semibold font-mono ${c}`}>{value}</div>
    </div></Card>
  );
}
